import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, DoorOpen, Target, Ban, ShieldAlert, Clock, Check } from "lucide-react";

/**
 * Récapitulatif des règles principales de consultation à valider explicitement
 * avant la confirmation définitive du rendez-vous.
 * Points 1, 5, 6, 7, 8 de la politique des consultations (résumés).
 */
export function ConsultationPolicyModal({ open, onOpenChange, onAccept, loading }) {
  const [accepted, setAccepted] = useState(false);

  const rules = [
    {
      icon: DoorOpen,
      color: "#D926A9",
      title: "Accès à la boutique (pour les consultations sur place)",
      text: "Ne pas arriver en avance. À l'heure prévue, présente-toi à la porte rouge « Bilan 2 Vie – Entrée ». Les accompagnants ne sont pas acceptés en séance."
    },
    {
      icon: Target,
      color: "#00E5FF",
      title: "Focalisation de la séance",
      text: "La consultation porte exclusivement sur toi ou une personne directement liée à ta problématique. Les questions intrusives sur des tiers sans lien direct ne sont pas abordées."
    },
    {
      icon: Ban,
      color: "#FF6B6B",
      title: "Réservé aux personnes majeures",
      text: "Les consultations sont strictement interdites aux mineurs, même avec l'accord ou la présence d'un parent."
    },
    {
      icon: ShieldAlert,
      color: "#FFD700",
      title: "Sujets exclus",
      text: "Ne sont pas abordés : la santé, le décès, et les questions intrusives sur la vie privée d'un tiers."
    },
    {
      icon: Clock,
      color: "#9D4CDD",
      title: "Annulation",
      text: "Modification ou annulation gratuite jusqu'à 3 heures avant le RDV. Au-delà, la séance est due (sauf cas exceptionnel justifié)."
    },
  ];

  const handleClose = (next) => {
    if (loading) return;
    if (!next) setAccepted(false);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#261B3B] border border-white/15 text-[#E2D5F8] w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100svh-1rem)] sm:h-auto sm:max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="consultation-policy-modal">
        <DialogHeader className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-[#D926A9]/15 border border-[#D926A9]/30 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-[#D926A9]" />
            </div>
            <DialogTitle className="font-serif text-lg sm:text-xl text-[#F7F3FC] leading-tight text-left">Avant de confirmer ta réservation</DialogTitle>
          </div>
          <DialogDescription className="text-[#B59ED1] text-xs sm:text-sm text-left">
            Merci de prendre connaissance des règles essentielles de la consultation.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4" data-testid="policy-scroll-area">
          <div className="space-y-4">
            {rules.map((rule, idx) => {
              const Icon = rule.icon;
              return (
                <div key={rule.title || idx} className="flex gap-3 items-start" data-testid={`policy-rule-${idx}`}>
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${rule.color}18`, border: `1px solid ${rule.color}40` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: rule.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-[#F7F3FC] text-sm mb-0.5">{rule.title}</h4>
                    <p className="text-[#B59ED1] text-sm leading-relaxed">{rule.text}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-white/5">
            <p className="text-xs text-[#B59ED1] text-center">
              Politique complète :{" "}
              <Link to="/politique-consultations" target="_blank" className="text-[#00E5FF] hover:underline" data-testid="policy-full-link">
                voir tous les détails
              </Link>
            </p>
          </div>
        </div>

        <div className="p-4 sm:p-6 pt-3 sm:pt-4 border-t border-white/10 flex-shrink-0 space-y-3 bg-[#261B3B]">
          <label className="flex items-start gap-2 cursor-pointer text-sm text-[#E2D5F8] leading-snug">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded accent-[#D926A9] flex-shrink-0"
              data-testid="policy-accept-checkbox"
            />
            <span>J'ai lu et j'accepte la politique des consultations de Bilan 2 Vie.</span>
          </label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={loading}
              className="border-white/15 text-[#B59ED1] hover:text-white hover:bg-white/5 flex-shrink-0"
              data-testid="policy-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={onAccept}
              disabled={!accepted || loading}
              className="flex-1 gradient-primary"
              data-testid="policy-confirm-btn"
            >
              {loading ? "Réservation..." : (<><Check className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Confirmer la réservation</span><span className="sm:hidden">Confirmer</span></>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
